'use client';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { createSupabaseBrowserClient } from '@/lib/supabase/client';

import { QueryKeys } from '@/constants/query-keys';

/** How long to wait for the realtime channel to connect before assuming it's unavailable. */
const SUBSCRIBE_GRACE_MS = 8_000;
/** Fallback refetch cadence when realtime isn't working. */
const POLL_INTERVAL_MS = 30_000;

/**
 * Subscribes to Postgres changes on `team_members`. Without this, a location
 * switch made by one branch only shows up on other already-open screens
 * (e.g. the calendar) after a manual reload, since React Query only refetches
 * team members on its own staleTime/mount schedule.
 *
 * Some tenants (e.g. self-hosted stacks) may not have Realtime enabled on
 * their Postgres instance — the channel then never reaches `SUBSCRIBED` and
 * never errors either, it just stays silent. Falls back to polling in that
 * case, and whenever the channel drops, so sync still works everywhere.
 *
 * Mount once at the app root — it is a singleton by design.
 */
export function useTeamMembersRealtimeSync() {
  const queryClient = useQueryClient();

  useEffect(() => {
    const supabase = createSupabaseBrowserClient();
    let pollTimer: ReturnType<typeof setInterval> | null = null;
    let subscribed = false;

    const invalidate = () =>
      queryClient.invalidateQueries({ queryKey: [QueryKeys.TEAM_MEMBERS] });

    const startPolling = () => {
      if (pollTimer) return;
      pollTimer = setInterval(invalidate, POLL_INTERVAL_MS);
    };

    const stopPolling = () => {
      if (!pollTimer) return;
      clearInterval(pollTimer);
      pollTimer = null;
    };

    const channel = supabase
      .channel('team-members-realtime-sync')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'team_members' },
        invalidate,
      )
      .subscribe((status: string) => {
        if (status === 'SUBSCRIBED') {
          subscribed = true;
          stopPolling();
        } else if (['CHANNEL_ERROR', 'TIMED_OUT', 'CLOSED'].includes(status)) {
          startPolling();
        }
      });

    const graceTimer = setTimeout(() => {
      if (!subscribed) startPolling();
    }, SUBSCRIBE_GRACE_MS);

    return () => {
      clearTimeout(graceTimer);
      stopPolling();
      supabase.removeChannel(channel);
    };
  }, [queryClient]);
}
